import { v4 as uuidv4 } from 'uuid';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { updateCart, setCartLoading } from '../../../../utils/cart/cartUtils';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { dataLayerPushServiceEvent } from '../utils/utils';
import { AddServiceOfferingsInput, BuildInstallSuggestionParams, Facets, VesaPattern } from './types';
import {
  CABLE_HIDING_SKU,
  INSTALL_INCLUSIONS_AND_EXCLUSIONS_PATH,
  INSTALL_TERMS_PATH,
  OLD_APPLIANCE_REMOVAL_SKU,
} from '../../../../utils/constants/services';
import { OfferingsProps, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { getRemovalOfferingOfInstallService } from '../utils/product-service';

const storefrontClient = new ShopifyStorefrontClient();

const addServiceOfferings = async (input: AddServiceOfferingsInput) => {
  const { offerings, primaryLineItem, shouldDrawCart = true } = input;

  if (!offerings.length) {
    return undefined;
  }

  try {
    setCartLoading(true);
    const existingServiceLink = primaryLineItem.properties?._serviceLink;

    const serviceLinkId = existingServiceLink && existingServiceLink !== '' ? existingServiceLink : uuidv4();

    primaryLineItem.properties = {
      ...primaryLineItem.properties,
      _isAttachedService: 'false',
      _serviceLink: serviceLinkId,
    };

    const primaryLineItemProperties = {
      ...primaryLineItem.properties,
      _isAttachedService: 'false',
      _serviceLink: serviceLinkId,
    };

    await storefrontClient.updateCartLine(
      primaryLineItem.line_id,
      undefined,
      filterAndMapAttributes(primaryLineItemProperties)
    );

    const serviceLines = offerings.map(offering => {
      const { variantId, location, message, serviceData: serviceDataObject } = offering;
      const { requiredServiceData, ...serviceData } = serviceDataObject;

      const serviceProperties = {
        _serviceLink: serviceLinkId,
        _isAttachedService: 'true',
        _serviceData: JSON.stringify(serviceData),
        _serviceSource: location,
        _requiredServiceData: JSON.stringify(requiredServiceData),
        _servicePrice: serviceData.priceInc.toString(),
        _serviceDate: serviceData.extraCareCoveragePeriod,
        _serviceType: serviceData.typeId,
        _priceRange: JSON.stringify(serviceData.priceRange),
      };

      return {
        line: {
          variantId,
          quantity: 1,
          attributes: filterAndMapAttributes(serviceProperties),
          parentId: primaryLineItem.line_id,
        },
        tracking: {
          location,
          message,
          serviceData,
        },
      };
    });

    const cart = await storefrontClient.addMultipleToCart(serviceLines.map(({ line }) => line));

    if (shouldDrawCart) {
      await updateCart(cart);
    } else {
      setCartLoading(false);
    }

    serviceLines.forEach(({ tracking }) => {
      const { serviceData, location, message } = tracking;
      dataLayerPushServiceEvent(
        `${serviceData.typeId}.addedtocart`,
        primaryLineItem?.onlineProduct?.ProductTags?.GoogleTagManager,
        {
          sku: serviceData.sku,
          name: serviceData.offeringName,
          price: serviceData.priceInc,
        },
        location,
        message
      );
    });

    return cart;
  } catch (e) {
    window.console.error(e);
  }
};

const normaliseSku = (sku?: string | number | null): string | undefined => (sku != null ? String(sku) : undefined);

const parseSkuList = (skusString: string | undefined): Array<string> => {
  if (!skusString) return [];
  return Array.from(
    new Set(
      skusString
        .split(/[,\s]+/)
        .map(s => normaliseSku(s.trim()))
        .filter((s): s is string => !!s)
    )
  );
};

const findProductBySku = (products: Array<AlgoliaProductHit>, sku?: string): AlgoliaProductHit | undefined => {
  const targetSku = normaliseSku(sku);
  if (!targetSku) return undefined;

  return products.find(product => {
    const productSku = normaliseSku(product?.sku);
    if (productSku === targetSku) return true;

    const alternates = (product?.skus ?? []).map(normaliseSku).filter(Boolean) as Array<string>;
    return alternates.includes(targetSku);
  });
};

const isProductAvailable = (product: AlgoliaProductHit): boolean => {
  const { availability } = product;
  if (!availability) return false;

  return availability.displayProduct && availability.canBuyOnline && product.price > 0 && product.product_published;
};

const normalizeFacetKey = (key: string) =>
  key
    .replace(/\s+/g, ' ')
    .replace(/[()]/g, '')
    .replace(/[^\w\s.-]/g, '')
    .trim()
    .toLowerCase();

const getFacetValues = (facets: Facets, targets: Array<string>): Array<string | number> => {
  if (!facets) return [];
  const normalizedTargets = targets.map(normalizeFacetKey);

  for (const [rawKey, values] of Object.entries(facets)) {
    const key = normalizeFacetKey(rawKey);
    if (normalizedTargets.includes(key)) {
      return Array.isArray(values) ? values : [];
    }
  }

  return [];
};

const toNumber = (value: string | number | undefined): number | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;

  const match = value.match(/-?\d+(?:\.\d+)?/);
  if (!match) return undefined;
  return Number(match[0]);
};

const parseNumericValue = (values: Array<string | number>): number | undefined => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed != null) return parsed;
  }
  return undefined;
};

const parseVesaPatternsFromValue = (value: string | number): Array<VesaPattern> => {
  if (typeof value !== 'string') return [];

  const patterns: Array<VesaPattern> = [];
  const normalisedValue = value.replace(/[×xX]/g, 'x');
  const regex = /(\d+(?:\.\d+)?)\s*mm?\s*x\s*(\d+(?:\.\d+)?)\s*mm?/gi;

  let match: RegExpExecArray | null;
  while ((match = regex.exec(normalisedValue)) !== null) {
    const width = Number(match[1]);
    const height = Number(match[2]);
    if (width && height) {
      patterns.push({ width, height });
    }
  }

  if (patterns.length) return patterns;

  const numericValues = normalisedValue.match(/\d+(?:\.\d+)?/g);
  if (!numericValues || numericValues.length < 2) return [];

  const width = Number(numericValues[0]);
  const height = Number(numericValues[1]);

  return [{ width, height }];
};

const parseVesaPattern = (values: Array<string | number>): VesaPattern | undefined => {
  for (const value of values) {
    const [pattern] = parseVesaPatternsFromValue(value);
    if (pattern) return pattern;
  }
  return undefined;
};

const parseTvSpecs = (facets: Facets) => {
  const screenSizeValues = getFacetValues(facets, ['Screen Size (Inches)']);
  const screenSize = screenSizeValues.map(toNumber).find(value => value != null);

  const vesaValues = getFacetValues(facets, ['VESA wall mount type']);
  const vesaPatterns = vesaValues.flatMap(parseVesaPatternsFromValue);

  const weightValues = getFacetValues(facets, ['Product Weight without Stand (kg)']);
  const weightKg = parseNumericValue(weightValues);

  return {
    screenSize,
    vesaPatterns,
    weightKg,
  };
};

const parseBracketSpecs = (facets: Facets) => {
  const minSizeValues = getFacetValues(facets, ['Minimum TV size (inches)']);
  const maxSizeValues = getFacetValues(facets, ['Maximum TV size (inches)']);

  const minSize = minSizeValues.map(toNumber).find(value => value != null);
  const maxSize = maxSizeValues.map(toNumber).find(value => value != null);

  const minVesaValues = getFacetValues(facets, ['Minimum VESA pattern size']);
  const maxVesaValues = getFacetValues(facets, ['Maximum VESA pattern Size']);

  const minVesa = parseVesaPattern(minVesaValues);
  const maxVesa = parseVesaPattern(maxVesaValues);

  const maxWeightValues = getFacetValues(facets, ['Maximum TV weight (kg)']);
  const maxWeightKg = parseNumericValue(maxWeightValues);

  return {
    minSize,
    maxSize,
    minVesa,
    maxVesa,
    maxWeightKg,
  };
};

const isVesaWithinRange = (pattern: VesaPattern, min: VesaPattern, max: VesaPattern): boolean => {
  const patternArr = [pattern.width, pattern.height];
  const minArr = [min.width, min.height];
  const maxArr = [max.width, max.height];

  return patternArr.every((dimension, index) => {
    return dimension >= minArr[index] && dimension <= maxArr[index];
  });
};

const matchesTvScreenSize = (product: AlgoliaProductHit, tvFacets?: Facets): boolean => {
  const tvSpecs = parseTvSpecs(tvFacets);
  const bracketSpecs = parseBracketSpecs(product.facets);

  // 1️⃣ Screen size check
  if (tvSpecs.screenSize == null || bracketSpecs.minSize == null || bracketSpecs.maxSize == null) {
    return false;
  }

  if (tvSpecs.screenSize < bracketSpecs.minSize || tvSpecs.screenSize > bracketSpecs.maxSize) {
    return false;
  }

  // 2️⃣ VESA pattern check
  const { minVesa, maxVesa } = bracketSpecs;
  if (!tvSpecs.vesaPatterns?.length) {
    return false;
  }
  if (!minVesa || !maxVesa) {
    return false;
  }

  const hasCompatibleVesa = tvSpecs.vesaPatterns.some(pattern => isVesaWithinRange(pattern, minVesa, maxVesa));
  if (!hasCompatibleVesa) {
    return false;
  }

  // 3️⃣ Weight check
  if (tvSpecs.weightKg == null || bracketSpecs.maxWeightKg == null) {
    return false;
  }

  if (tvSpecs.weightKg > bracketSpecs.maxWeightKg) {
    return false;
  }

  return true;
};

const selectPreferredProducts = (
  products: Array<AlgoliaProductHit>,
  skuList: Array<string>,
  predicate: (product: AlgoliaProductHit) => boolean,
  options: { limit?: number } = {}
): Array<AlgoliaProductHit> => {
  if (!products.length || !skuList.length) return [];

  const selected: Array<AlgoliaProductHit> = [];
  const seen = new Set<string>();

  for (const sku of skuList) {
    if (options.limit && selected.length >= options.limit) break;

    const product = findProductBySku(products, sku);
    if (!product) continue;
    if (seen.has(product.objectID)) continue;
    if (!predicate(product)) continue;

    selected.push(product);
    seen.add(product.objectID);
  }

  return selected;
};

const enrichInstallService = ({
  service,
  tvInstallGroups,
}: BuildInstallSuggestionParams): {
  enrichedService: ServicesProps;
  removalOfferingOfInstallService: OfferingsProps | undefined;
} => {
  const groups = tvInstallGroups ?? {};

  const offerings = service.offerings ?? [];

  const removalOfferingOfInstallService = getRemovalOfferingOfInstallService(service);

  const offeringsBySku = new Map<string, OfferingsProps>();
  const enhancedOfferingsBySku = new Map<string, OfferingsProps>();

  offerings.forEach(offering => {
    if (offering.sku && offering.id !== removalOfferingOfInstallService?.id) {
      offeringsBySku.set(offering.sku, offering);
    }
  });

  Object.values(groups).forEach(group => {
    const groupSkus = group?.skus?.filter(Boolean) ?? [];
    if (!groupSkus.length) return;
    if (groupSkus.length === 1) {
      const sku = groupSkus[0];
      if (!sku) return;
      const baseOffering = offeringsBySku.get(sku);
      if (!baseOffering) return;
      enhancedOfferingsBySku.set(sku, {
        ...baseOffering,
        title: group?.title,
        heading: group?.title,
        description: group?.description,
        subText: group?.description,
        explained: group?.explained,
        skusIncluded: [sku],
        priceToShow: baseOffering.priceInc,
      });
      return;
    }

    const [primarySkuRaw] = groupSkus;
    const primarySku = primarySkuRaw;
    const cableSku = groupSkus.find(sku => sku === CABLE_HIDING_SKU) || groupSkus[1];
    if (!primarySku || !cableSku) return;

    const primaryOffering = offeringsBySku.get(primarySku);
    const cableOffering = offeringsBySku.get(cableSku);

    if (!primaryOffering || !cableOffering) return;

    enhancedOfferingsBySku.set(cableSku, {
      ...cableOffering,
      title: group?.title ?? cableOffering.title,
      heading: group?.title ?? cableOffering.title,
      description: group?.description ?? cableOffering.description,
      subText: group?.description ?? cableOffering.description,
      explained: group?.explained ?? cableOffering.explained,
      skusIncluded: [primarySku, cableSku],
      priceToShow: primaryOffering.priceInc + cableOffering.priceInc,
    });
  });

  const updatedOfferings = offerings.reduce<Array<OfferingsProps>>((acc, offering) => {
    if (offering.sku === OLD_APPLIANCE_REMOVAL_SKU) return acc;
    const enhanced = offering.sku ? enhancedOfferingsBySku.get(offering.sku) : undefined;
    acc.push(enhanced ?? offering);
    return acc;
  }, []);

  updatedOfferings.sort((a, b) => {
    const priceA = typeof a.priceToShow === 'number' ? a.priceToShow : a.priceInc;
    const priceB = typeof b.priceToShow === 'number' ? b.priceToShow : b.priceInc;
    if (priceA === priceB) return 0;
    return priceB - priceA;
  });

  const enrichedService: ServicesProps = { ...service, offerings: updatedOfferings };

  return { enrichedService, removalOfferingOfInstallService };
};

export {
  addServiceOfferings,
  parseSkuList,
  findProductBySku,
  isProductAvailable,
  matchesTvScreenSize,
  selectPreferredProducts,
  INSTALL_INCLUSIONS_AND_EXCLUSIONS_PATH,
  INSTALL_TERMS_PATH,
  enrichInstallService,
  normaliseSku,
  CABLE_HIDING_SKU as cableHidingSku,
};
